<template>
    <div ref="componentRef" class="min-h-screen bg-white">
        <!-- Search Bar Container - Positioned to align with navbar -->
        <div class="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
            <div class="flex justify-end mb-2">
                <div class="flex items-center border-b-2 border-gray-300 max-w-sm w-full">
                    <input type="text" v-model="searchQuery"
                        placeholder="Search events by title, description, or location" @keydown.enter="handleSearch"
                        @input="handleSearch"
                        class="flex-1 bg-transparent text-primary placeholder-gray-400 border-none outline-none text-sm font-body mr-3 text-left" />
                    <button v-if="searchQuery.trim()" @click="clearSearch"
                        class="p-1.5 text-gray-700 hover:text-gray-900 transition-colors flex-shrink-0"
                        aria-label="Clear search">
                        <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <button @click="handleSearch"
                        class="p-1.5 bg-accent text-white hover:bg-green-700 transition-colors flex-shrink-0"
                        aria-label="Search">
                        <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <!-- Main Content Container -->
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="text-center mb-2.5">
                <h1 class="font-header text-[3rem] md:text-[3rem] font-bold text-primary mb-1.5">
                    Events
                </h1>
                <div class="font-body text-[1.125rem] text-gray-700 max-w-4xl mx-auto leading-[1.75] content-page-description"
                    v-html="pageDescription || 'Join us for author readings, book launches, and literary events throughout the year.'">
                </div>
            </div>

            <div v-if="loading" class="max-w-7xl mx-auto text-center py-3">
                <Loader size="large" />
            </div>
            <div v-else-if="searchQuery.trim()" class="max-w-7xl mx-auto">
                <!-- Search Results View -->
                <div class="mb-1.5">
                    <div class="flex items-center justify-between mb-1.5">
                        <h2 class="font-header text-[1.5rem] md:text-[1.5rem] font-bold text-primary">
                            Search Results
                        </h2>
                        <span class="font-body text-[1.125rem] text-gray-600 leading-[1.75]">
                            {{ allEvents.length }} {{ allEvents.length === 1 ? 'event' : 'events' }} found
                        </span>
                    </div>
                    <div v-if="allEvents.length > 0" class="space-y-1.5">
                        <div v-for="event in allEvents" :key="event.id"
                            class="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                            @click="goToEvent(event.id)">
                            <div class="flex flex-col md:flex-row gap-1.5 items-center md:items-start">
                                <div v-if="event.image"
                                    class="flex-shrink-0 w-full md:w-64 max-w-full overflow-hidden rounded-lg flex justify-center md:justify-start"
                                    :style="getEventImageContainerStyle(event, 256)">
                                    <img :src="event.image" :alt="event.title" :style="getEventImageStyle(event)"
                                        class="w-full h-auto md:h-full object-cover max-w-full" />
                                </div>
                                <div v-else class="flex-shrink-0">
                                    <div
                                        class="w-24 h-24 bg-accent rounded-lg flex flex-col items-center justify-center text-white">
                                        <span class="font-header text-[1.5rem] font-bold">{{ event.date.day }}</span>
                                        <span class="font-header text-[1.125rem] uppercase">{{ event.date.month
                                        }}</span>
                                    </div>
                                </div>
                                <div class="flex-1 p-2.5">
                                    <h3 class="font-header text-[1.5rem] font-bold text-primary mb-1" data-no-translate>
                                        {{ event.title }}
                                    </h3>
                                    <div
                                        class="flex flex-wrap items-center gap-2 font-body text-[1.125rem] text-gray-600 leading-[1.75] mb-1">
                                        <span>{{ formatEventDate(event.date.fullDate) }}</span>
                                        <span class="text-gray-400">|</span>
                                        <span>{{ event.time }}</span>
                                        <span class="text-gray-400">|</span>
                                        <span>{{ event.location }}</span>
                                    </div>
                                    <div class="font-body text-[1.125rem] text-gray-700 leading-[1.75] mb-1 content-page-section"
                                        v-html="event.description">
                                    </div>
                                    <div v-if="event.featured" class="flex items-center gap-2 mb-1">
                                        <span
                                            class="px-3 py-1 bg-accent text-white rounded text-sm font-header font-semibold">
                                            Featured
                                        </span>
                                    </div>
                                    <button v-if="event.rsvp" @click.stop="goToEvent(event.id)"
                                        class="bg-accent text-white px-6 py-2 rounded-md hover:bg-green-600 transition-colors font-header font-medium">
                                        Get Tickets
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="text-center py-8">
                        <p class="font-body text-[1.125rem] text-gray-600 leading-[1.75]">No events found matching your
                            search.</p>
                    </div>
                </div>
            </div>
            <div v-else class="max-w-7xl mx-auto">
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-1.5 mb-1.5">
                    <div class="lg:col-span-2 order-2 lg:order-1">
                        <EventCalendar :events="allEvents" />
                    </div>
                    <div class="lg:col-span-1 order-1 lg:order-2">
                        <div class="bg-white rounded-lg shadow-sm p-2 md:p-3 sticky top-8">
                            <h2 class="font-header text-[1.5rem] md:text-[1.5rem] font-bold text-primary mb-1.5">
                                Upcoming Events
                            </h2>
                            <div class="space-y-1">
                                <div v-for="event in upcomingEvents" :key="event.id" @click="goToEvent(event.id)"
                                    class="p-2 bg-gray-50 rounded-md hover:bg-gray-100 cursor-pointer transition-colors border-l-4 border-accent">
                                    <div class="flex items-start gap-1.5 mb-1 overflow-hidden min-w-0">
                                        <h3 class="font-header font-bold text-primary text-[1.5rem] leading-tight flex-1 min-w-0"
                                            data-no-translate>
                                            {{ event.title }}
                                        </h3>
                                        <div v-if="event.featured" class="flex-shrink-0">
                                            <span
                                                class="px-2 py-0.5 bg-accent text-white rounded text-xs font-header font-semibold">
                                                Featured
                                            </span>
                                        </div>
                                    </div>
                                    <div
                                        class="flex items-center gap-2 font-body text-[1.125rem] text-gray-600 leading-[1.75] mb-1">
                                        <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span>{{ formatEventDate(event.date.fullDate) }}</span>
                                    </div>
                                    <div
                                        class="flex items-center gap-2 font-body text-[1.125rem] text-gray-600 leading-[1.75] mb-1">
                                        <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span>{{ event.time }}</span>
                                    </div>
                                    <div
                                        class="flex items-center gap-2 font-body text-[1.125rem] text-gray-600 leading-[1.75]">
                                        <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <span class="truncate">{{ event.location }}</span>
                                    </div>
                                </div>
                                <div v-if="upcomingEvents.length === 0"
                                    class="text-center py-1 text-gray-500 font-body text-[1.125rem] leading-[1.75]">
                                    No upcoming events scheduled
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mb-1.5">
                    <h2 class="font-header text-[1.5rem] md:text-[1.5rem] font-bold text-primary mb-1.5">
                        Event Details
                    </h2>
                    <div class="space-y-1.5">
                        <div v-for="event in paginatedUpcomingEvents" :key="event.id"
                            class="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                            @click="goToEvent(event.id)">
                            <div class="flex flex-col md:flex-row gap-1.5 items-center md:items-start">
                                <div v-if="event.image"
                                    class="flex-shrink-0 w-full md:w-64 max-w-full overflow-hidden rounded-lg flex justify-center md:justify-start"
                                    :style="getEventImageContainerStyle(event, 256)">
                                    <img :src="event.image" :alt="event.title" :style="getEventImageStyle(event)"
                                        class="w-full h-auto md:h-full object-cover max-w-full" />
                                </div>
                                <div v-else class="flex-shrink-0">
                                    <div
                                        class="w-24 h-24 bg-accent rounded-lg flex flex-col items-center justify-center text-white">
                                        <span class="font-header text-[1.5rem] font-bold">{{ event.date.day }}</span>
                                        <span class="font-header text-[1.125rem] uppercase">{{ event.date.month
                                        }}</span>
                                    </div>
                                </div>
                                <div class="flex-1 p-2.5">
                                    <h3 class="font-header font-bold text-primary text-[1.5rem] mb-1" data-no-translate>
                                        {{ event.title }}
                                    </h3>
                                    <div
                                        class="flex flex-wrap items-center gap-2 font-body text-[1.125rem] text-gray-600 leading-[1.75] mb-1">
                                        <span>{{ formatEventDate(event.date.fullDate) }}</span>
                                        <span class="text-gray-400">|</span>
                                        <span>{{ event.time }}</span>
                                        <span class="text-gray-400">|</span>
                                        <span>{{ event.location }}</span>
                                    </div>
                                    <div class="font-body text-[1.125rem] text-gray-700 leading-[1.75] mb-1 content-page-section"
                                        v-html="event.description">
                                    </div>
                                    <div v-if="event.featured" class="flex items-center gap-2 mb-1">
                                        <span
                                            class="px-3 py-1 bg-accent text-white rounded text-sm font-header font-semibold">
                                            Featured
                                        </span>
                                    </div>
                                    <button v-if="event.rsvp" @click.stop="goToEvent(event.id)"
                                        class="bg-accent text-white px-6 py-2 rounded-md hover:bg-green-600 transition-colors font-header font-medium">
                                        Get Tickets
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div v-if="allUpcomingEvents.length === 0"
                            class="text-center py-1 text-gray-500 font-body text-[1.125rem] leading-[1.75]">
                            No upcoming events scheduled
                        </div>
                    </div>
                    <div v-if="totalUpcomingPages > 1"
                        class="mt-1.5 flex justify-between items-center border-t border-gray-200 pt-1.5">
                        <button @click="currentUpcomingPage = Math.max(1, currentUpcomingPage - 1)"
                            :disabled="currentUpcomingPage === 1"
                            :class="['px-4 py-2 rounded-md font-header transition-colors', currentUpcomingPage === 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-green-700 text-white hover:bg-green-800']">
                            Previous
                        </button>
                        <span class="font-header text-gray-700">
                            Page {{ currentUpcomingPage }} of {{ totalUpcomingPages }} ({{ allUpcomingEvents.length }}
                            total)
                        </span>
                        <button @click="currentUpcomingPage = Math.min(totalUpcomingPages, currentUpcomingPage + 1)"
                            :disabled="currentUpcomingPage >= totalUpcomingPages"
                            :class="['px-4 py-2 rounded-md font-header transition-colors', currentUpcomingPage >= totalUpcomingPages ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-green-700 text-white hover:bg-green-800']">
                            Next
                        </button>
                    </div>
                </div>

                <div v-if="pastEvents.length > 0" class="mt-2.5">
                    <h2 class="text-3xl md:text-4xl font-marketing font-bold text-primary mb-1.5">
                        Past Events
                    </h2>
                    <div class="space-y-1.5">
                        <div v-for="event in pastEvents" :key="event.id"
                            class="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                            <div class="flex flex-col md:flex-row gap-1.5 items-center md:items-start">
                                <div v-if="event.image"
                                    class="flex-shrink-0 w-full md:w-48 max-w-full overflow-hidden rounded-lg flex justify-center md:justify-start"
                                    :style="getEventImageContainerStyle(event, 192)">
                                    <img :src="event.image" :alt="event.title" :style="getEventImageStyle(event)"
                                        class="w-full h-auto md:h-full object-cover max-w-full" />
                                </div>
                                <div v-else class="flex-shrink-0">
                                    <div
                                        class="w-20 h-20 bg-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-600">
                                        <span class="text-xl font-marketing font-bold">{{ event.date.day }}</span>
                                        <span class="text-xs font-marketing uppercase">{{ event.date.month }}</span>
                                    </div>
                                </div>
                                <div class="flex-1 p-2 md:p-3">
                                    <h3 class="text-xl font-marketing font-bold text-primary mb-1" data-no-translate>
                                        {{ event.title }}
                                    </h3>
                                    <p class="text-gray-600 font-sans mb-1">
                                        {{ event.date.full }} • {{ event.location }}
                                    </p>
                                    <div class="text-gray-700 font-sans content-page-section"
                                        v-html="event.description">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-2.5 bg-white rounded-lg shadow-sm p-2.5 md:p-4 text-center" data-newsletter>
                    <h2 class="text-3xl md:text-4xl font-header font-bold text-primary mb-1.5">
                        Stay Updated
                    </h2>
                    <p class="text-lg text-gray-700 font-body mb-1.5 max-w-2xl mx-auto">
                        Subscribe to our newsletter to receive updates about upcoming events, author signings, and book
                        launches.
                    </p>
                    <div class="flex flex-col sm:flex-row gap-1 max-w-md mx-auto overflow-hidden min-w-0">
                        <input type="email" placeholder="Enter your email"
                            class="flex-1 px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body min-w-0" />
                        <button
                            class="bg-accent text-white px-8 py-3 rounded-md hover:bg-green-600 transition-colors font-marketing font-semibold whitespace-nowrap flex-shrink-0">
                            Subscribe
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import EventCalendar from './EventCalendar.vue'
import { publicService } from '../services/public'
import Loader from './Loader.vue'
import { useLoadingState } from '../composables/useLoadingState'
import { useEventImageStyle } from '../composables/useEventImageStyle'
import { useDataTranslation } from '../composables/useDataTranslation'
import { formatTime, getESTNow, convertToESTDate, normalizeApiDate } from '../utils/date'

const { getEventImageStyle, getEventImageContainerStyle } = useEventImageStyle()

const allEvents = ref([])
const searchQuery = ref('')
const isSearching = ref(false)
const { loading, setLoading } = useLoadingState(true)
const pageDescription = ref('')
const componentRef = ref(null)
const currentUpcomingPage = ref(1)
const eventsPerPage = 10

const { translatePage, handleLanguageChange: handleTranslation } = useDataTranslation(componentRef, loading, '.min-h-screen.bg-white', setLoading)

const handleLanguageChange = async () => {
    await loadEvents()
    await loadPageDescription()
    await handleTranslation()
}

onMounted(() => {
    window.addEventListener('language-changed', handleLanguageChange)
})

onUnmounted(() => {
    window.removeEventListener('language-changed', handleLanguageChange)
})

function transformEvent(apiEvent) {
    const normalizedDate = normalizeApiDate(apiEvent.date)
    const eventDate = normalizedDate ? new Date(normalizedDate) : null
    if (!eventDate || isNaN(eventDate.getTime())) {
        return null
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        day: '2-digit',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    })
    const parts = formatter.formatToParts(eventDate)
    const day = parts.find(p => p.type === 'day').value
    const month = parts.find(p => p.type === 'month').value
    const timeStr = formatTime(normalizedDate)

    return {
        ...apiEvent,
        date: {
            day,
            month,
            fullDate: normalizedDate
        },
        time: timeStr,
        location: apiEvent.location || 'TBA',
        ticketPrice: apiEvent.ticket_price || 0,
        maxTickets: apiEvent.max_attendees || 0,
        requiresRSVP: !!apiEvent.ticket_price,
        rsvp: !!apiEvent.ticket_price,
        image_crop_dimensions: apiEvent.image_crop_dimensions || null
    }
}

const allUpcomingEvents = computed(() => {
    const now = getESTNow()
    return allEvents.value.filter(event => {
        if (!event.date?.fullDate) return false
        const eventDate = convertToESTDate(event.date.fullDate)
        if (!eventDate) return false
        return eventDate >= now
    })
        .sort((a, b) => {
            const dateA = convertToESTDate(a.date.fullDate)
            const dateB = convertToESTDate(b.date.fullDate)
            if (!dateA || !dateB) return 0
            return dateA - dateB
        })
})

const upcomingEvents = computed(() => {
    return allUpcomingEvents.value.slice(0, 3)
})

const totalUpcomingPages = computed(() => {
    return Math.ceil(allUpcomingEvents.value.length / eventsPerPage)
})

const paginatedUpcomingEvents = computed(() => {
    const start = (currentUpcomingPage.value - 1) * eventsPerPage
    const end = start + eventsPerPage
    return allUpcomingEvents.value.slice(start, end)
})

const pastEvents = computed(() => {
    const now = getESTNow()
    return allEvents.value.filter(event => {
        if (!event.date?.fullDate) return false
        const eventDate = convertToESTDate(event.date.fullDate)
        if (!eventDate) return false
        return eventDate < now
    })
})

const loadEvents = async () => {
    setLoading(true)
    isSearching.value = false
    searchQuery.value = ''
    try {
        const data = await publicService.getEvents()
        const events = Array.isArray(data) ? data : []
        allEvents.value = events.map(transformEvent).filter(event => event !== null)
        currentUpcomingPage.value = 1
    } catch (error) {
        console.error('Failed to load events:', error)
        allEvents.value = []
    } finally {
        setLoading(false)
    }
}

const handleSearch = async () => {
    if (!searchQuery.value.trim()) {
        loadEvents()
        return
    }

    setLoading(true)
    isSearching.value = true
    try {
        const data = await publicService.searchEvents(searchQuery.value.trim())
        const events = Array.isArray(data?.data) ? data.data : []
        allEvents.value = events.map(transformEvent).filter(event => event !== null)
        currentUpcomingPage.value = 1
    } catch (error) {
        console.error('Failed to search events:', error)
        allEvents.value = []
    } finally {
        setLoading(false)
    }
}

const clearSearch = () => {
    searchQuery.value = ''
    loadEvents()
}

function goToEvent(eventId) {
    window.location.href = `/events/${eventId}`
}

function formatEventDate(dateStr) {
    if (!dateStr) return 'Date TBA'
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return 'Invalid date'
    const currentYear = new Date().getFullYear()
    const eventYear = new Date(dateStr).getFullYear()
    const options = {
        month: 'short',
        day: 'numeric',
        timeZone: 'America/New_York'
    }
    if (eventYear !== currentYear) {
        options.year = 'numeric'
    }
    const formatted = date.toLocaleDateString('en-US', options).replace(',', '')
    return formatted
}

const loadPageDescription = async () => {
    try {
        const page = await publicService.getPage('Events')
        if (page && page.description) {
            pageDescription.value = page.description
        }
    } catch (error) {
        console.error('Failed to load page description:', error)
    }
}

onMounted(async () => {
    await loadEvents()
    await loadPageDescription()
    await translatePage()
})
</script>

<style scoped></style>
